<template>
  <q-dialog class="dialog">
    <q-card :style="$isDesktop ? 'min-width: 600px' : ''">
      <q-card-section class="row items-center" :class="{'q-px-lg': $isDesktop}">
        <div class="heading-3 q-mr-sm">Projectdeelnemers beheren</div>
        <q-space />
        <icon-button icon="close" aria-label="Sluit dialoogvenster" size="sm" round v-close-popup />
      </q-card-section>
      <q-card-section class="dialog__messages" :class="$isMobile ? 'q-pb-none' : 'q-px-lg q-pt-none q-pb-sm'">
        <h4 class="q-mb-md">Project gebruikers</h4>
        <q-list v-if="users.length" bordered separator class="rounded-borders"
                style="max-height: 33vh; overflow-y: auto;">
          <template v-for="user in users" :key="user.id">
            <q-item>
              <q-item-section>
                <q-item-label>
                  {{ $fullName(user) }}
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <icon-button
                    v-if="users.length > 1 && !!user.id"
                    icon="close"
                    color="neutral-70"
                    aria-label="Verwijder gebruiker"
                    size="sm"
                    round
                    @click="onDeleteUser(user)"
                />
                <div v-else>
                  Laatste gebruiker
                </div>
              </q-item-section>
            </q-item>
          </template>
        </q-list>
      </q-card-section>
      <q-card-section class="dialog__messages" :class="$isMobile ? 'q-pb-none' : 'q-px-lg q-pt-none q-pb-sm'">
        <h4 class="q-mb-md">Voeg een nieuwe gebruiker toe</h4>
        <p>
          Voer de naam in van een bestaande gebruiker of het emailadres van
          een nog niet geregistreerde gebruiker
        </p>
        <common-input v-model="term"
                      @update:modelValue="searchUser(term)"
                      :loading="loadingUser"
                      label="Naam of e-mailadres"
                      clearable
        />
        <q-list v-if="term" bordered separator class="rounded-borders">
          <template v-for="result in searchResults" :key="result.id">
            <q-item clickable
                    :disable="!result.canBeAdded"
                    @click="addUser(result)"
                    :title="result.canBeAdded ? `Voeg ${$fullName(result)} toe` : `${$fullName(result)} neemt al deel`">
              <q-item-section>
                <q-item-label>
                  {{ $fullName(result) }}
                  <template v-if="!result.canBeAdded">
                      (neemt al deel)
                  </template>
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
          <q-item v-if="!searchResults.length">
            <q-item-section>
              <q-item-label>
                Deze gebruiker is niet bekend
              </q-item-label>
            </q-item-section>
            <q-item-section>
              <q-btn v-if="termIsEmail"
                     color="primary"
                     @click="addNewUser(term)">
                Uitnodigen
              </q-btn>
            </q-item-section>
          </q-item>
        </q-list>
      </q-card-section>

      <q-separator />
      <q-card-actions :class="$isMobile ? 'q-pa-md' : 'q-px-lg q-py-md'">
        <slot name="actions">
          <flat-button
              label="Sluiten"
              v-close-popup
          />
        </slot>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<script>

export default {
  emits: ['update:users'],
  props: {
    projectId: {
      type: Number,
      required: true,
    },
    users: {
      type: Array,
      required: false,
      default: null,
    },
  },
  computed: {
    termIsEmail () {
      return /[^\s]+@[^\s]+\.[^\s]+/.test(this.term)
    },
  },
  data: () => ({
    term: '',
    searchResults: [],
    loadingUser: false,
  }),
  methods: {
    searchUser: _.debounce(async function (term) {
      if (term !== this.term) return
      this.loadingUser = true
      const { data } = await axios.get(this.$route('api.user'), {
        params: { term },
      })
      this.searchResults = data.map(result => ({
        ...result,
        canBeAdded: !this.users.find(user => user.id === result.id)
      }))
      this.loadingUser = false
    }, 500),
    async addUser (user) {
      if(!user.canBeAdded) return
      try {
        await axios.post(this.$route('project.user.store', { project_id: this.projectId }), {
          user_id: user.id,
        })
        this.$emit('update:users', [...this.users, user])
        this.$toast('success', 'Gebruiker is toegevoegd')
        this.term = ''
        this.searchResults = []
      } catch (error) {
        console.error(error)
        this.$toast('error', 'Er is iets mis gegaan')
      }
    },
    async addNewUser (email) {
      try {
        await axios.post(this.$route('project.user.store', { project_id: this.projectId }), {
          name: email,
        })
        this.$emit('update:users', [...this.users, {
          first_name: email,
          last_name: '',
        }])
        this.$toast('success', 'Gebruiker is uitgenodigd')
        this.term = ''
        this.searchResults = []
      } catch (error) {
        console.error(error)
        this.$toast('error', 'Er is iets mis gegaan')
      }
    },
    async onDeleteUser (deletedUser) {
      if (!deletedUser.id || deletedUser.id !== this.currentUser.id || await this.$confirm(
          {
            title: 'Gebruiker verwijderen',
            messages: ['Weet u zeker dat u uzelf uit dit project wilt verwijderen? U verliest daarbij uw toegang tot deze pagina.'],
          },
      )) {
        try {
          await axios.delete(this.$route('project.user.remove', {
            project_id: this.projectId,
            user_id: deletedUser.id,
          }))
          this.$emit('update:users', this.users.filter(user => {
            return user.id !== deletedUser.id
          }))
          this.$toast('success', 'Gebruiker is verwijderd')
        } catch (error) {
          console.error(error)
          this.$toast('error', 'Er is iets mis gegaan')
        }
      }
    },
  },
}
</script>
